/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { DATE, formatDate } from '../utils/date';
import { UtrvStatus } from '../constants/status';
import {
  canExecuteAction,
  getIconByStatus,
  getInputValue,
  getLatestHistoryUser,
  getStatusIcon,
  getStatusText,
  isAssuredLocked,
  isNa,
  isNr,
  isUtrvAssuranceComplete,
} from '../utils/universalTrackerValue';
import { UniversalTrackerValuePlain, ValueHistory } from '../types/universalTrackerValue';
import { UserMin } from '@g17eco/types/user';
import { capitaliseFirstLetter } from '../utils';

type MinimalUtrv = Partial<UniversalTrackerValuePlain> & Pick<UniversalTrackerValuePlain,
  'status' |
  'effectiveDate' |
  'lastUpdated' |
  'universalTrackerId' |
  'initiativeId'
>;
export type NewUniversalTrackerValuePlain = Omit<MinimalUtrv, '_id'> & { _new: boolean };

export default class UniversalTrackerValue<T extends MinimalUtrv | NewUniversalTrackerValuePlain = MinimalUtrv | NewUniversalTrackerValuePlain> {
  universalTrackerValue;

  constructor(universalTrackerValue: T) {
    this.universalTrackerValue = universalTrackerValue;
  }

  getId() {
    if ('_new' in this.universalTrackerValue) {
      return '';
    }
    return this.universalTrackerValue._id;
  }

  getValueType() {
    return this.universalTrackerValue.valueType;
  }

  getUniversalTrackerId() {
    return this.universalTrackerValue.universalTrackerId;
  }

  isRejected() {
    return this.getStatus() === UtrvStatus.Rejected;
  }

  isEscalated() {
    return false;
    // This seems to be depdrecated
    // return this.universalTrackerValue.escalationPolicy && this.universalTrackerValue.escalationPolicy.isEscalated;
  }

  getStatus() {
    return this.universalTrackerValue.status;
  }

  getStatusText() {
    return getStatusText(this.universalTrackerValue);
  }

  getStatusIcon() {
    return getStatusIcon(this.universalTrackerValue);
  }

  getIconByStatus(status: string) {
    getIconByStatus(status);
  }

  isAssured() {
    return isUtrvAssuranceComplete(this.universalTrackerValue.assuranceStatus)
  }

  isAssuredLocked() {
    return isAssuredLocked(this.universalTrackerValue);
  }

  isNew() {
    // Temp hacky cast as any until we deprecate this bad pattern
    return '_new' in this.universalTrackerValue && this.universalTrackerValue._new;
  }

  isNA() {
    return isNa(this.universalTrackerValue);
  }

  isNR() {
    return isNr(this.universalTrackerValue);
  }

  setType(type: string) {
    this.universalTrackerValue.type = type;
  }

  getType() {
    return this.universalTrackerValue.type;
  }

  setEffectiveDate(date: string) {
    return this.universalTrackerValue.effectiveDate = date;
  }

  getUniversalTrackerValue() {
    return this.universalTrackerValue;
  }

  static getStatusTextByHistory(history: Pick<ValueHistory, 'action'>) {
    return capitaliseFirstLetter(history.action);
  }

  isComplete() {
    return this.getStatus() === UtrvStatus.Verified;
  }

  getInitiativeId() {
    if (!this.universalTrackerValue.initiativeId) {
      return;
    }
    return this.universalTrackerValue.initiativeId;
  }

  getInitiative() {
    if (!this.universalTrackerValue.initiativeId) {
      return '';
    }
    return {
      _id: this.universalTrackerValue.initiativeId,
      name: this.universalTrackerValue.initiativeId + ' Name'
    }
  }

  getValue() {
    return this.universalTrackerValue.value;
  }

  getInputValue() {
    return getInputValue(this.universalTrackerValue);
  }

  getLatestHistoryUser(users: Pick<UserMin, '_id' | 'firstName' | 'surname'>[]) {
    return getLatestHistoryUser(this.universalTrackerValue, users);
  }

  getLatestHistoryDate() {
    const latestHistory = this.getLatestHistory();
    if (!latestHistory) {
      return;
    }
    return formatDate(latestHistory.date);
  }

  getLatestHistoryNotes() {
    const latestHistory = this.getLatestHistory();
    if (!latestHistory) {
      return;
    }
    return latestHistory.note;
  }

  getDate(format = DATE.DEFAULT_SPACES) {
    return formatDate(this.universalTrackerValue.effectiveDate, format);
  }

  getStartDate(format = DATE.DEFAULT_SPACES) {
    return formatDate(this.universalTrackerValue.effectiveDate, format);
  }

  getSampleSize() {
    return this.universalTrackerValue.sampleSize;
  }

  getLastUpdated(format = DATE.LONG) {
    return formatDate(this.universalTrackerValue.lastUpdated, format);
  }

  canExecuteAction(action: string) {
    return canExecuteAction(this.universalTrackerValue, action);
  }

  isEvidenceRequired() {
    return this.universalTrackerValue.evidenceRequired === true;
  }

  isVerificationRequired() {
    return this.universalTrackerValue.verificationRequired === true;
  }

  isPrivate() {
    return this.universalTrackerValue.isPrivate === true;
  }

  hasHistory() {
    return Array.isArray(this.universalTrackerValue.history) && this.universalTrackerValue.history.length > 0;
  }

  getHistory() {
    const history = this.universalTrackerValue.history;

    if (!Array.isArray(history)) {
      return [];
    }

    history.sort((a, b) => {
      if (a.date > b.date) return -1;
      if (a.date < b.date) return 1;
      return 0;
    });

    return history;
  }

  getLatestHistory() {
    const history = this.getHistory();
    if (history.length >= 0) {
      return history[0];
    }
  }

  isOverdue() {
    return false;
  }
}
